import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { AbstractContentComponent } from '../../../../../abstract-content.component';
import { Context } from '@service/context.service';
import { Column } from '@shared/table/column';
import { ChopMassnahme, ChopMesskriterium, ChopProfilMassnahme } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { ChopMesskriteriumDialogComponent } from './chop-messkriterium-dialog/chop-messkriterium-dialog.component';
import { ChopProfilMassnahmeDialogComponent } from './chop-profil-massnahme-dialog/chop-profil-massnahme-dialog.component';
import { ActionButtonDirective } from '@shared/table/action-button.directive';
import { ToolbarButtonDirective } from '@shared/table/toolbar-button.directive';
import { TableComponent } from '@shared/table/table.component';
import { TabsModule } from 'primeng/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChopProfilDetailData } from './chop-profil-detail.resolve';

interface ProfilMassnahmeRow {
  tacsCodeKreisId: number;
  tacsId: string;
  bezeichnung: string;
}

@Component({
  selector: 'app-chop-profil-detail',
  templateUrl: './chop-profil-detail.component.html',
  styleUrls: ['./chop-profil-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TabsModule, TranslatePipe, TableComponent, ToolbarButtonDirective, ActionButtonDirective],
})
export class ChopProfilDetailComponent extends AbstractContentComponent implements OnInit {
  private chopService = inject(ChopService);

  private data = this.activatedRoute.snapshot.data.detail as ChopProfilDetailData;
  private profil = this.data.profil;
  private leistungsgruppe = this.data.leistungsgruppe;
  private alterskategorie = this.data.alterskategorie;
  private katalog = this.data.katalog;
  private allMassnahmen: ChopMassnahme[] = this.data.allMassnahmen;
  messkriterien: ChopMesskriterium[] = this.data.messkriterien;
  massnahmenRows: ProfilMassnahmeRow[] = this.mapMassnahmen(this.data.profilMassnahmen);
  messkriterienColumns: Column[] = [];
  massnahmenColumns: Column[] = [];
  activeTab = 'messkriterien';
  private profilId = this.profil.id;

  constructor() {
    super(Context.Global);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setColumns();
    this.refreshBreadcrumbs();
  }

  protected onLanguageChange(): void {
    this.setColumns();
    this.refreshBreadcrumbs();
    this.markForCheck();
  }

  reloadMesskriterien(): void {
    this.chopService.getMesskriterien(this.profilId).subscribe((messkriterien) => {
      this.messkriterien = messkriterien;
      this.markForCheck();
    });
  }

  reloadMassnahmen(): void {
    this.chopService.getProfilMassnahmen(this.profilId).subscribe((profilMassnahmen) => {
      this.massnahmenRows = this.mapMassnahmen(profilMassnahmen);
      this.markForCheck();
    });
  }

  showCreateMesskriterium(): void {
    this.openMesskriteriumDialog()?.onClose.subscribe((result) => {
      if (result) {
        this.reloadMesskriterien();
      }
    });
  }

  showEditMesskriterium(mk: ChopMesskriterium): void {
    this.openMesskriteriumDialog(mk)?.onClose.subscribe((result) => {
      if (result) {
        this.reloadMesskriterien();
      }
    });
  }

  openMesskriteriumDetail(mk: ChopMesskriterium): void {
    this.navigate(['/admin-chop/messkriterien', mk.id]);
  }

  deleteMesskriterium(mk: ChopMesskriterium): void {
    this.confirm({
      header: this.translate('CHOP.DELETE_MESSKRITERIUM_HEADER'),
      message: this.translate('CHOP.DELETE_MESSKRITERIUM_MESSAGE'),
      accept: () => {
        this.chopService.deleteMesskriterium(mk.id).subscribe({
          next: () => {
            this.showSuccessMessage();
            this.reloadMesskriterien();
          },
          error: (err) => this.showErrorMessage(undefined, err?.error?.message),
        });
      },
    });
  }

  showAddMassnahmeDialog(): void {
    this.dialogService
      .open(ChopProfilMassnahmeDialogComponent, {
        modal: true,
        closable: true,
        focusOnShow: false,
        width: '700px',
        header: this.translate('CHOP.CREATE_MASSNAHME'),
        data: {
          profilId: this.profilId,
          alreadyAssignedIds: this.massnahmenRows.map((r) => r.tacsCodeKreisId),
          allMassnahmen: this.allMassnahmen,
        },
      })
      ?.onClose.subscribe((result) => {
        if (result) {
          this.reloadMassnahmen();
        }
      });
  }

  removeMassnahme(row: ProfilMassnahmeRow): void {
    this.confirm({
      header: this.translate('CHOP.DELETE_MASSNAHME_HEADER'),
      message: this.translate('CHOP.DELETE_MASSNAHME_MESSAGE'),
      accept: () => {
        this.chopService.removeProfilMassnahme(this.profilId, row.tacsCodeKreisId).subscribe({
          next: () => {
            this.showSuccessMessage();
            this.reloadMassnahmen();
          },
          error: (err) => this.showErrorMessage(undefined, err?.error?.message),
        });
      },
    });
  }

  private openMesskriteriumDialog(messkriterium?: ChopMesskriterium): DynamicDialogRef | null {
    return this.dialogService.open(ChopMesskriteriumDialogComponent, {
      modal: true,
      closable: true,
      focusOnShow: false,
      width: '600px',
      header: this.translate(messkriterium ? 'CHOP.EDIT_MESSKRITERIUM' : 'CHOP.CREATE_MESSKRITERIUM'),
      data: { profilId: this.profilId, messkriterium },
    });
  }

  private mapMassnahmen(assignments: ChopProfilMassnahme[]): ProfilMassnahmeRow[] {
    const lookup = new Map(this.allMassnahmen.map((m) => [m.id, m]));
    return assignments.map((a) => {
      const massnahme = lookup.get(a.tacsCodeKreisId);
      return {
        tacsCodeKreisId: a.tacsCodeKreisId,
        tacsId: massnahme?.tacsId ?? '',
        bezeichnung: massnahme ? this.getLocalizedText(massnahme.bezeichnung) : '',
      };
    });
  }

  private refreshBreadcrumbs(): void {
    this.setBreadcrumbs([
      { label: this.translate('MENU.CHOP'), routerLink: ['/admin-chop'] },
      {
        label: `${this.katalog.jahr} ${this.getLocalizedText(this.katalog.bezeichnung)}`,
        routerLink: ['/admin-chop/kataloge', this.katalog.id],
      },
      {
        label: `${this.alterskategorie.code} ${this.getLocalizedText(this.alterskategorie.bezeichnung)}`,
        routerLink: ['/admin-chop/alterskategorien', this.alterskategorie.id],
      },
      {
        label: `${this.leistungsgruppe.nummer} ${this.getLocalizedText(this.leistungsgruppe.bezeichnung)}`,
        routerLink: ['/admin-chop/leistungsgruppen', this.leistungsgruppe.id],
      },
      { label: `${this.profil.nummer} ${this.getLocalizedText(this.profil.bezeichnung)}` },
    ]);
  }

  private setColumns(): void {
    this.messkriterienColumns = [
      { field: 'mindestAnzahl', headerKey: 'CHOP.MINDEST_ANZAHL', width: '160px', filterType: 'integer' },
      { field: 'mindestMinutenProTag', headerKey: 'CHOP.MINDEST_MIN_PRO_TAG', width: '180px', filterType: 'integer' },
      {
        field: 'mindestMinutenProDurchfuehrung',
        headerKey: 'CHOP.MINDEST_MIN_PRO_DURCHFUEHRUNG',
        width: '220px',
        filterType: 'integer',
      },
    ];
    this.massnahmenColumns = [
      { field: 'tacsId', headerKey: 'CHOP.CODE', width: '200px' },
      { field: 'bezeichnung', headerKey: 'CHOP.BEZEICHNUNG', minWidth: '300px' },
    ];
  }
}
